import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { ChartConfigurationOption } from '../../wtFramework';

import { TrendSeries } from './TrendSeries';
import { WideError, missingSeries, missingId, momentFormat, Roles } from '../../common';
import {
    AggregationMode,
    Dictionary,
    ErrorLevel,
    IValueViewModel,
    KeyValuePair,
    LogbookEntryDto,
    NameBuilderConfiguration,
    SelectorType,
    TagDateValue,
    TagDateValuesWithRange,
    Unit,
    VariableType,
    WidgetValues
} from '../../models';
import { ISeriesService, ILoggerService, IAuthorizationService } from '../../services';
import { SeriesMappingResult } from './';
import { mapConfiguration } from '../../common/helpers/valueViewModelHelper';
import { ISeriesBuilder, ISeriesBuilderArguments } from '../../services/widgets/ISeriesBuilder';
import { ILogbookService, IVariableService, IValuesService } from '../../services/data';
import { ILogbookEntryHelper } from '../../common/helpers/';
import { SeriesConfiguration } from '../../widgets';

import { lineChartLeftMargin, lineChartBottomMargin, lineChartTopMargin, lineChartRightMargin, IMargins, IAreaConfiguration } from '../../framework';
import { TagsTrendWidget } from '../../widgets/widgets.index';

export interface ITagsTrendService extends ISeriesBuilder<TrendSeries<TagDateValue>> {
    getSeries(
        seriesConfig: SeriesConfiguration[],
        nameConfiguration: NameBuilderConfiguration,
        viewModels: Dictionary<IValueViewModel>
    ): angular.IPromise<SeriesMappingResult<TrendSeries<TagDateValue>>>;
    getOptions(item: TagsTrendWidget): ChartConfigurationOption;
    getValues(configuration, boundData: WidgetValues): TagDateValuesWithRange;
    getEntryPanes(entries: LogbookEntryDto[]): IAreaConfiguration[];
    read(
        seriesConfiguration: { guid: string; value: string }[],
        beginDate: string,
        endDate: string,
        hideMissing?: boolean,
        force?: boolean
    ): angular.IPromise<any>;
    getReadPromise(
        seriesConfiguration: { guid: string; value: string }[],
        beginMoment: moment.Moment,
        endMoment: moment.Moment,
        hideMissing?: boolean,
        force?: boolean
    ): angular.IPromise<any>;
}

export class TagsTrendService implements ITagsTrendService {
    static serviceId = 'tagsTrendService';

    static $inject = [
        '$q',
        'authorizationService',
        'seriesService',
        'logbookService',
        'loggerService',
        'logbookEntryHelper',
        'variableService',
        'valuesService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _authorizationService: IAuthorizationService,
        private _seriesService: ISeriesService,
        private _logbookService: ILogbookService,
        private _loggerService: ILoggerService,
        private _logbookEntryHelper: ILogbookEntryHelper,
        private _variableService: IVariableService,
        private _valuesService: IValuesService
    ) {}

    getSeries(
        seriesConfig: SeriesConfiguration[],
        nameConfiguration: NameBuilderConfiguration,
        viewModels: Dictionary<IValueViewModel>
    ): angular.IPromise<SeriesMappingResult<TrendSeries<TagDateValue>>> {
        return this._seriesService.createSeriesList<TagDateValue>(seriesConfig, nameConfiguration, viewModels).then((series) => {
            let error = null;
            if (_.some(series, (s) => !s.name)) {
                error = new WideError(ErrorLevel.Error, missingSeries);
            }
            const result = series.filter((s) => s.name);
            const missingSerie = _.find(series, (s) => s.identifier === missingId);
            if (!missingSerie) {
                result.push(this._seriesService.createMissingSerie());
            }
            return {
                error,
                series: result,
                map: {}
            };
        });
    }

    getOptions(item: TagsTrendWidget): ChartConfigurationOption {
        const { configuration } = item;
        let options: ChartConfigurationOption = configuration.options ? angular.copy(configuration.options) : null;
        if (!options) {
            options = new ChartConfigurationOption();
            try {
                options.isTitleVisible = true;
                options.display = {};
            } catch (e) {
                this._loggerService.logError(e);
            }
        }

        if (!options.display) {
            options.display = {};
        }
        if (options.display.xMin) {
            options.display.xMin = moment(options.display.xMin).toDate();
        }
        if (options.display.xMax) {
            options.display.xMax = moment(options.display.xMax).toDate();
        }
        if (!options.panes) {
            options.panes = [];
        }
        if (options.panes.length === 0) {
            const pane: any = angular.copy(options);
            pane.height = 100;
            pane.areas = [];
            options.panes.push(pane);
        }
        const pane = options.panes[0];
        pane.xMin = options.display.xMin;
        pane.xMax = options.display.xMax;
        pane.yMin = options.display.yMin;
        pane.yMax = options.display.yMax;
        pane.isTitleVisible = options.isTitleVisible;
        // watch the case differences
        pane.isHorizontalGridLinesVisible = options.isHorizontalGridlinesVisible;
        pane.isLegendVisible = options.isLegendVisible;
        pane.title = options.title;
        const margins: IMargins = {
            marginLeft: item.showLeftMargin !== false && item.configuration.options.showYAxis !== false ? lineChartLeftMargin : 0,
            marginRight: item.showRightMargin !== false ? lineChartRightMargin : 0,
            marginTop: item.showTopMargin !== false ? lineChartTopMargin : 0,
            marginBottom: item.showBottomMargin !== false && item.configuration.options.showXAxis !== false ? lineChartBottomMargin : 0
        };
        pane.margins = margins;
        return options;
    }

    getValues(configuration, boundData: WidgetValues): { data: TagDateValue[]; range: { beginDate: Date; endDate: Date } } {
        const values = !configuration.aggregation || configuration.aggregation.mode === AggregationMode.None ? boundData.values : boundData.aggregatedValues;
        let data = values ? values : [];
        if (values) {
            data = values.concat(this.updateMissingSeries(boundData.missing));
        }
        return {
            data: data,
            range: {
                beginDate: moment(boundData.beginDate).toDate(),
                endDate: moment(boundData.endDate).toDate()
            }
        };
    }

    // Create pane to display logbook entries
    // Since #27374 we want to group mathcing logbook entries by start date
    getEntryPanes(entries?: LogbookEntryDto[]): IAreaConfiguration[] {
        if (!entries) {
            entries = [];
        }

        const groupedEntries = _.groupBy(
            entries,
            (e) => `${moment(e.beginDateTime).format(momentFormat)}`
            // as suggested by #33486 only aggregate on start date
            // -${moment(e.endDateTime).format(momentFormat)}
        );

        const result: IAreaConfiguration[] = [];

        for (const k of Object.keys(groupedEntries)) {
            const e = _.sortBy(groupedEntries[k], [(e) => e.severity, (e) => e.orderId]).reverse();
            result.push(this.mapMatchingEntriesInPane(e));
        }

        return _.sortBy(result, (e) => e.iconContent);
    }

    private mapMatchingEntriesInPane(entries: LogbookEntryDto[]): IAreaConfiguration & { guid: number } {
        const maxEntry = _.maxBy(entries, (e) => e.severity);
        const maxEntries = entries.filter((e) => e.severity === maxEntry.severity);
        const entry = maxEntries.length > 1 ? _.minBy(maxEntries, (entry) => entry.orderId) : maxEntry;
        const begin = moment(entry.beginDateTime);
        const end = entry.endDateTime ? moment(entry.endDateTime) : undefined;

        entries.forEach((e) => {
            // remap label as a unique string to be passed to multiline
            (e as any).categories = '';
            if (e.labels && e.labels.length > 0) {
                (e as any).categories = e.labels.join(', ');
            }
        });

        const title = this._logbookService.getMultiLineTitleHtml(entries);

        const retval: IAreaConfiguration & { guid: number } = {
            beginDate: begin.toDate(),
            color: this._logbookEntryHelper.logbookEntryLevelColor(entry.severity),
            endDate: end?.toDate(),
            guid: entry.logbookEntryId,
            iconContent: entry.orderId,
            iconSize: 7,
            iconTitle: title,
            isHidingDatesOnHover: true,
            name: '',
            opacity: 0.05,
            showBoundaries: true
        };
        if (entries.length === 1) {
            try {
                const entry = entries[0];
                entry.range = { startDate: entry.beginDateTime, endDate: entry.endDateTime };
                const badge = this._logbookService.getBadge(entries, entry.beginDateTime);
                const hasManualInputRole = this._authorizationService.isInRoles(Roles.manualInputRoles);
                retval.onClick = (d) => {
                    this._logbookService.openModal(entry.identifier?.guid, badge, true, hasManualInputRole, true, {
                        startDate: entry.beginDateTime,
                        endDate: entry.endDateTime
                    });
                };
            } catch (e) {
                console.error(e);
            }
        }
        return retval;
    }

    buildSeriesFromViewModels(args: ISeriesBuilderArguments): angular.IPromise<SeriesMappingResult<TrendSeries<TagDateValue>>> {
        const { configuration, viewModels, map, errors } = args;
        const seriesConfig = configuration.series as SeriesConfiguration[];
        const areaFilter = seriesConfig ? seriesConfig.filter((s) => s.seriesType !== 'area') : [];
        const constantFilter = areaFilter.filter((s) => s.type !== SelectorType.constant);
        const mapped = mapConfiguration(constantFilter, viewModels, map, errors);
        const error = mapped.error;
        const hasConversion = configuration.options && configuration.options.variableTypeGuid && configuration.options.unitGuid;
        let promise: angular.IPromise<{ variable: VariableType; unit: Unit } | null>;
        if (hasConversion) {
            promise = this._variableService.getByGuid(configuration.options.variableTypeGuid).then((data) => {
                const unit = data.unitCollection.filter((u) => u.guid === configuration.options.unitGuid)[0];
                return {
                    variable: data,
                    unit
                };
            });
        } else {
            promise = this._$q.when(null);
        }
        const nameConfiguration = configuration.nameConfiguration;
        return promise.then((data) => {
            const hasXAxis = mapped.series.filter((s) => !s.missing && (s.configuration as SeriesConfiguration).isXAxis).length > 0;
            const fromServer = mapped.series
                .filter((s) => !s.missing)
                .map((s) => {
                    const vm = angular.copy(s.viewModel);
                    if (data && data.variable.guid === vm.variableTypeGuid) {
                        vm.unitName = data.unit.name;
                    }
                    const name = this._seriesService.getName(s.configuration as SeriesConfiguration, vm, nameConfiguration);
                    return this._seriesService.createSeries(s.configuration as SeriesConfiguration, name, vm, hasXAxis);
                });

            const items = areaFilter
                .filter((a) => a.type === SelectorType.constant)
                .map((a) => {
                    const res: any = a;
                    res.name = '';
                    return res;
                });
            const result = fromServer.filter((d) => !!d && !!d.identifier).concat(items);
            if (!configuration.hideMissing && !configuration.hideMissingRanges) {
                result.push(this._seriesService.createMissingSerie());
            }
            return { error, series: result, map };
        });
    }

    getReadPromise(
        seriesConfiguration: { guid: string; value: string }[],
        beginMoment: moment.Moment,
        endMoment: moment.Moment,
        hideMissing: boolean = false,
        force: boolean = false
    ): angular.IPromise<any> {
        const diff = endMoment.diff(beginMoment, 'day');
        const beginDate = beginMoment.toISOString();
        const endDate = endMoment.toISOString();
        if (diff < 30) {
            return this.read(seriesConfiguration, beginDate, endDate, hideMissing, force)
                .then((data) => {
                    return data;
                })
                .catch((reason) => {
                    if (reason.exceptionType === 'System.OutOfMemoryException') {
                        this._loggerService.logError('This amount of data cannot be retrieved. Try decreasing the period you are looking at.');
                    }
                });
        } else {
            return this._valuesService
                .getCompressedValues(seriesConfiguration, beginDate, endDate, force)
                .then((data) => {
                    return { values: data, compressed: true };
                })
                .catch((reason) => {
                    if (reason.exceptionType === 'System.OutOfMemoryException') {
                        this._loggerService.logError('This amount of data cannot be retrieved. Try decreasing the period you are looking at.');
                    }
                });
        }
    }

    read(
        seriesConfiguration: { guid: string; value: string }[],
        beginDate: string,
        endDate: string,
        hideMissing: boolean = false,
        force: boolean = false
    ): angular.IPromise<any> {
        return hideMissing
            ? this._valuesService.getValues(seriesConfiguration, beginDate, endDate, force).then((data) => {
                  return { values: data };
              })
            : this._valuesService.getValuesWithMissing(seriesConfiguration, beginDate, endDate, force);
    }

    private updateMissingSeries(missing: KeyValuePair<string, any[]>[]): any[] {
        return this._seriesService.updateMissingSeries(missing);
    }
}
